import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Leaf } from 'lucide-react';

interface SharedNavbarProps {
  onLogin?: () => void;
  onGetStarted?: () => void;
}

const SharedNavbar: React.FC<SharedNavbarProps> = ({ onLogin, onGetStarted }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogin = () => {
    if (onLogin) {
      onLogin();
    } else {
      // Default behavior - navigate to login
      window.location.href = '/login';
    }
  };

  const handleGetStarted = () => {
    if (onGetStarted) {
      onGetStarted();
    } else {
      // Default behavior - navigate to register
      window.location.href = '/register';
    }
  };

  return (
    <>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-emerald-100 shadow-sm" style={{ backgroundColor: 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo with green pill background */}
            <Link to="/" className="flex items-center">
              <div className="flex items-center gap-3 bg-emerald-600 text-white px-4 py-2 rounded-lg shadow-md">
                <Leaf className="h-5 w-5" />
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-white">{t('branding.appName')}</span>
                  <span className="text-xs text-white/90 hidden sm:block">{t('branding.tagline')}</span>
                </div>
              </div>
            </Link>

            {/* Center navigation links */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/funkcijos" className="text-gray-600 hover:text-emerald-600 transition-colors">
                {t('navigation.features')}
              </Link>
              <Link to="/kainos" className="text-gray-600 hover:text-emerald-600 transition-colors">
                {t('navigation.pricing')}
              </Link>
              <Link 
                to="/apie-mus" 
                className={`transition-colors ${
                  location.pathname === '/apie-mus' 
                    ? 'text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-md' 
                    : 'text-gray-600 hover:text-emerald-600'
                }`}
              >
                {t('navigation.about')}
              </Link>
            </div>

            {/* Right side buttons */}
            <div className="hidden md:flex items-center space-x-4">
              <Button
                variant="outline"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 bg-transparent"
                onClick={handleLogin}
              >
                {t('navigation.login')}
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleGetStarted}>
                {t('hero.getStarted')}
              </Button>
              {/* Language switcher */}
              <Button variant="ghost" size="sm" className="text-gray-600 hover:text-emerald-600">
                EN
              </Button>
            </div>

            {/* Mobile menu button */}
            <Button variant="ghost" size="sm" className="md:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span className="w-4 h-0.5 bg-gray-600 mb-1"></span>
                <span className="w-4 h-0.5 bg-gray-600 mb-1"></span>
                <span className="w-4 h-0.5 bg-gray-600"></span>
              </div>
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-b border-emerald-100">
          <div className="px-4 py-6 space-y-4">
            <Link to="/funkcijos" className="block text-gray-600 hover:text-emerald-600 transition-colors py-2">
              {t('navigation.features')}
            </Link>
            <Link to="/kainos" className="block text-gray-600 hover:text-emerald-600 transition-colors py-2">
              {t('navigation.pricing')}
            </Link>
            <Link 
              to="/apie-mus" 
              className={`block transition-colors py-2 ${
                location.pathname === '/apie-mus' 
                  ? 'text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-md' 
                  : 'text-gray-600 hover:text-emerald-600'
              }`}
            >
              {t('navigation.about')}
            </Link>
            <div className="pt-4 space-y-3">
              <Button
                variant="outline"
                className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 bg-transparent"
                onClick={handleLogin}
              >
                {t('navigation.login')}
              </Button>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleGetStarted}>
                {t('hero.getStarted')}
              </Button>
              <Button variant="ghost" className="w-full text-gray-600 hover:text-emerald-600">
                EN
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SharedNavbar; 